import { COOKIE_MOVIL } from "@/lib/movil/entrada"

/**
 * Si la página se está viendo dentro de una app de Zindra, no en un navegador.
 *
 * - **Escritorio** (Zindra Work): lo pone el propio programa en su agente de
 *   usuario, `ZindraWork/…`.
 * - **Móvil** (iOS y Android): la vista web la abre `/api/movil/entrada`, que
 *   deja la cookie `zindra_movil`; además la vista inyecta `window.mobileApp`.
 *
 * **Para qué importa:** dentro de las apps no se rastrea a nadie. No se carga
 * analítica de terceros ni se enseña el aviso de cookies (la persona ya ha
 * entrado con su cuenta y las únicas cookies son las de su sesión). Es lo que
 * App Store exige sin App Tracking Transparency, y lo que se declara a Apple.
 */
export const MARCA_DE_ESCRITORIO = "ZindraWork/"

/**
 * Lo que la app abre en el navegador integrado (por ejemplo «Crear cuenta»)
 * lleva `?desde=app`: ese navegador no tiene ni la cookie ni `mobileApp`. Se
 * recuerda durante la visita, para las páginas que vengan después.
 */
const DESDE_LA_APP = "zindra_desde_app"

export function estoyEnLaApp(): boolean {
  if (typeof window === "undefined") return false
  try {
    if (new URLSearchParams(window.location.search).get("desde") === "app") {
      try {
        sessionStorage.setItem(DESDE_LA_APP, "1")
      } catch {}
      return true
    }
    if (sessionStorage.getItem(DESDE_LA_APP) === "1") return true
    if (navigator.userAgent.includes(MARCA_DE_ESCRITORIO)) return true
    if (typeof (window as any).mobileApp?.postMessage === "function") return true
    return document.cookie.split(";").some((c) => c.trim().startsWith(`${COOKIE_MOVIL}=`))
  } catch {
    return false
  }
}

/** Qué aceptó la persona en el aviso de cookies (`cookie_consent_v1`). */
export const CLAVE_CONSENTIMIENTO = "cookie_consent_v1"
export const CONSENTIMIENTO_CAMBIADO = "zindra:consentimiento"

export function aceptoAnalitica(): boolean {
  if (typeof window === "undefined") return false
  try {
    const guardado = JSON.parse(localStorage.getItem(CLAVE_CONSENTIMIENTO) ?? "null")
    return Boolean(guardado?.analytics)
  } catch {
    return false
  }
}

/**
 * Las páginas de entrar y crear cuenta no enseñan el aviso de cookies.
 *
 * Ahí solo hay cookies técnicas (la sesión) y la analítica no se carga sin
 * consentimiento, así que no hay nada que preguntar. Y es por donde entra quien
 * viene de la app de iOS: «Crear cuenta» se abre en el navegador integrado,
 * que no comparte lo aceptado en Safari ni sabe que viene de la app.
 */
const PAGINAS_DE_ACCESO =
  /^\/(en\/)?(login|register|forgot-password|reset-password|set-password|verify-email|invite|auth|setup-account)(\/|$)/

export function esPaginaDeAcceso(ruta: string): boolean {
  return PAGINAS_DE_ACCESO.test(ruta)
}
